import React, { useContext, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { TaskCreateUpdateSchema } from "../../utils/validations";
import { InitiativeContext } from "../../contexts/InitiativeContextProvider";
import {
  createSubTask,
  getCustomFields,
  updateSubTask,
  getAllRisksWithFilters,
  getAllLessonsWithParams,
  getAllTaskComments,
} from "../../api/Api";
import { showToast } from "../../utils/toastUtils";
import CreateUpdateTaskModalUI from "./CreateUpdateTaskModalUI";

const priorities = [
  { name: "High", value: "High" },
  { name: "Medium", value: "Medium" },
  { name: "Low", value: "Low" },
];

function AddUpdateSubTask({
  show,
  handleClose,
  subTask,
  task,
  is_new_task = true,
  loadAll,
  projectId,
  workStreamId,
}) {
  const { data: workStreamData } = useContext(InitiativeContext);
  const [assignees, setAssignees] = useState([]);
  const [workStreams, setWorkStreams] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [activeStep, setActiveStep] = React.useState(0);
  const [risks, setRisks] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [comments, setComments] = useState([]);
  const [freshSubTask, setFreshSubTask] = useState(null); // Add freshSubTask state

  const task_id = is_new_task ? freshSubTask?.id : subTask?.id;

  useEffect(() => {
    async function fetchCustomFields() {
      if (!projectId) return; // Ensure projectId is available

      try {
        const response = await getCustomFields(projectId); // Fetch custom fields for the project
        const cf = response?.data?.custom_fields || [];
        const mcf = cf.map((field) => {
          const existingField = subTask?.custom_fields?.find(
            (cf) => cf.id === field.id
          );
          return {
            ...field,
            value: is_new_task ? "" : existingField?.value ?? "", // add value from existing subtask or empty string for new task
          };
        });
        setCustomFields(mcf);
      } catch (error) {
        console.error("Error fetching custom fields:", error);
      }
    }

    fetchCustomFields();
  }, [projectId, subTask]);

  console.log("customFields", customFields);

  // Initialize due date based on whether it's a new task or an existing subtask
  const initialDueDate = useMemo(() => {
    if (is_new_task) return null;
    return subTask?.due_date || new Date();
  }, [is_new_task, subTask?.due_date]);

  // Initialize Formik
  const formik = useFormik({
    initialValues: {
      title: is_new_task ? "" : subTask?.title || "",
      priorities:
        !is_new_task && subTask?.priority ? { name: subTask.priority } : null,
      due_date: initialDueDate,
      description: is_new_task ? "" : subTask?.description || "",
      ...customFields.reduce((acc, field) => {
        acc[field.name] = field.value;
        return acc;
      }, {}),
    },
    validationSchema: TaskCreateUpdateSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validate: (values) => {
      const errors = {};
      
      // Custom validation for description (strip HTML tags)
      const descriptionText = values.description ? values.description.replace(/<[^>]*>/g, '').trim() : '';
      if (!descriptionText) {
        errors.description = "Description is required";
      }
      
      return errors;
    },
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      handleSubmit(values, resetForm);
    },
  });

  const handleSubmit = async (values, closePop = false) => {
    console.log("values", values);
    formik.setSubmitting(true);

    // Check for empty description (strip HTML tags)
    const descriptionText = values.description ? values.description.replace(/<[^>]*>/g, '').trim() : '';
    const isDescriptionEmpty = !descriptionText;

    // Validate required fields
    const hasErrors = formik.errors.title || formik.errors.due_date;
    const isPriorityMissing = !values.priorities;
    const isDescriptionMissing = isDescriptionEmpty;

    if (hasErrors || isPriorityMissing || isDescriptionMissing) {
      showToast("Please fill all the required fields", "error");
      formik.setSubmitting(false);
      return;
    }

    const obj = {
      title: values.title,
      priority: values.priorities?.name || null,
      due_date: values.due_date,
      description: values.description,
      assignee_ids: assignees.map(({ id }) => id),
      workStream_ids: workStreamId
        ? [workStreamId]
        : workStreams.map(({ id }) => id),
      custom_fields: customFields.map((field) => ({
        id: field.id,
        name: field.name,
        value: values[field.name],
      })),
    };

    try {
      let response;

      if (!task_id) {
        // Creating a new subtask
        response = await createSubTask(task.id, obj);
        if (response.status === 201) {
          showToast("Task created successfully", "success");
          setFreshSubTask(response.data);
        }
      } else {
        // Updating an existing subtask
        response = await updateSubTask(task.id, subTask.id, obj);
        if (response.status === 200) {
          showToast("Task updated successfully", "success");
          is_new_task ? setFreshSubTask(response.data) : handleClose();
        }
      }

      // Refresh the task list
      loadAll();

      if (closePop) {
        handleClose();
      }
    } catch (error) {
      console.error("Error submitting subtask:", error);
      showToast("Failed to create/update task", "error");
    } finally {
      formik.setSubmitting(false);
    }
  };

  const fetchRisks = async () => {
    const targetTaskId = is_new_task ? task?.id : subTask?.id;

    if (targetTaskId) {
      try {
        const response = await getAllRisksWithFilters(
          null,
          null,
          null,
          null,
          targetTaskId
        );
        if (response.status === 200) {
          setRisks(response.data);
        }
      } catch (error) {
        console.error("Error fetching risks:", error);
      }
    }
  };

  useEffect(() => {
    fetchRisks();
  }, [task, subTask, is_new_task]);

  const fetchLessons = async () => {
    const targetTaskId = task_id || (is_new_task ? task?.id : subTask?.id);

    if (targetTaskId) {
      try {
        const response = await getAllLessonsWithParams(
          null,
          null,
          null,
          null,
          targetTaskId
        );
        if (response.status === 200) {
          setLessons(response.data);
        }
      } catch (error) {
        console.error("Error fetching lessons:", error);
      }
    }
  };

  useEffect(() => {
    fetchLessons();
  }, [task, subTask, is_new_task, task_id]);

  const fetchComments = async () => {
    const targetTaskId = is_new_task ? task?.id : subTask?.id;

    if (targetTaskId) {
      try {
        const response = await getAllTaskComments(targetTaskId);
        if (response.status === 200) {
          setComments(response.data);
        }
      } catch (error) {
        console.error("Error fetching comments:", error);
      }
    }
  };

  useEffect(() => {
    fetchComments();
  }, [task, subTask, is_new_task]);

  checkAdditionalCustomFields(customFields, subTask);

  return (
    <CreateUpdateTaskModalUI
      show={show}
      handleClose={handleClose}
      formik={formik}
      is_new_task={is_new_task}
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      priorities={priorities}
      workStreamData={workStreamData}
      workStreamId={workStreamId}
      assignees={assignees}
      setAssignees={setAssignees}
      workStreams={workStreams}
      setWorkStreams={setWorkStreams}
      handleSubmit={handleSubmit}
      task={subTask}
      task_id={task_id}
      customFields={is_new_task ? customFields : subTask?.custom_fields}
      is_subtask={true}
      risks={risks}
      fetchRisks={fetchRisks}
      lessons={lessons}
      fetchLessons={fetchLessons}
      comments={comments}
      fetchComments={fetchComments}
    />
  );
}

export default AddUpdateSubTask;

// check if there is additional custom fields
export const checkAdditionalCustomFields = (customFields, task = {}) => {
  if (!task?.custom_fields) return;
  customFields.forEach((field) => {
    const existingField = task.custom_fields.find((cf) => cf.id === field.id);
    if (!existingField) {
      task.custom_fields.push({
        id: field.id,
        name: field.name,
        type: field.type,
        value: null,
      });
    }
  });

  return task;
};
